import { Component, HostListener, computed, effect, inject, OnInit, signal } from '@angular/core';
import { RouterOutlet, RouterModule } from '@angular/router';
import { DrawerModule } from 'primeng/drawer'; // Cambiado de Sidebar a Drawer en v18/21
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { Sidebar } from './sidebar/sidebar';
import { AvatarModule } from 'primeng/avatar';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Core } from '../_core/core';
import { HomeS } from '../_core/home';
import { Notification } from '../_core/notification';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AuthService } from '../_core/auth.service';
import { BrandingService } from '../_core/branding.service';
import { WelcomeTour } from '../welcome-tour/welcome-tour';
import { welcomeTourFeatures } from '../welcome-tour/welcome-tour-features';
import { WelcomeTourSession } from '../welcome-tour/welcome-tour-session';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    BadgeModule,
    RouterModule,
    DrawerModule,
    ButtonModule,
    ToolbarModule,
    AvatarModule,
    OverlayBadgeModule,
    Sidebar,
    WelcomeTour,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['../../styles.scss', './layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  private $core = inject(Core);
  private $home = inject(HomeS);
  private $auth = inject(AuthService);
  private $branding = inject(BrandingService);
  public $notification = inject(Notification);
  private router = inject(Router);
  private readonly welcomeTour = inject(WelcomeTourSession);

  public sidebarVisible: boolean = false;
  public isMenuOpen: boolean = false;
  public greeting: string = '';

  public building = this.$core.selectedBuilding;
  public userData = this.$auth.userData;
  public homeData = this.$home.homeData;

  public readonly isHomeRoute = signal(this.isHomeUrl(this.router.url));
  private tourMenuCaptured = false;
  private menuOpenBeforeTour = false;

  public readonly tourFeatures = computed(() =>
    welcomeTourFeatures(
      {
        hasBilling: this.homeData()?.hasBilling === true,
        hasGateway: this.homeData()?.hasGateway === true,
        hasAlarmModuleAccess: this.homeData()?.hasAlarmModuleAccess === true,
        suppliers: this.homeData()?.suppliers ?? [],
      },
      this.$branding.isGGM(),
      this.$branding.sendRequestsEnabled(),
    ),
  );

  public readonly showWelcomeTour = computed(
    () =>
      this.isHomeRoute() &&
      this.$branding.isGGM() &&
      this.homeData() != null &&
      this.welcomeTour.pending(),
  );

  public readonly showAddPhonePrompt = computed(
    () =>
      !!this.building() &&
      this.$branding.isGGM() &&
      this.homeData()?.hasElectronicCommunications === false,
  );

  /** Nombre a mostrar en el saludo: propiedad seleccionada o nombre del usuario. */
  public welcomeName = computed(() => {
    const buildingName = this.building()?.tenantName?.trim();
    if (buildingName) return buildingName;
    return this.userData()?.fullname?.trim() || '';
  });

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.isHomeRoute.set(this.isHomeUrl(navigation.urlAfterRedirects));
        if (!this.showWelcomeTour()) {
          this.closeMenu();
        }
      });

    effect(() => {
      if (!this.showWelcomeTour()) {
        return;
      }
      this.openMenuForTour();
    });
  }

  ngOnInit(): void {
    this.greeting = this._getGreeting();
    void this.restoreSessionState();
  }

  /** Rehidrata edificio + home tras F5/HMR para que el menú no se quede incompleto. */
  private async restoreSessionState(): Promise<void> {
    try {
      await this.$core.ensureLoaded();

      if (this.$core.selectedBuilding() && !this.$home.homeData()) {
        await this.$home.fetchHomeData();
      }
    } catch (error) {
      console.error('Error restoring session state:', error);
    }
  }

  private isHomeUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/home' || path === '/home/';
  }

  private isNarrowViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  }

  private openMenuForTour(): void {
    if (!this.isNarrowViewport()) {
      return;
    }
    if (!this.tourMenuCaptured) {
      this.menuOpenBeforeTour = this.isMenuOpen;
      this.tourMenuCaptured = true;
    }
    this.isMenuOpen = true;
  }

  private _getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }

  public completeWelcomeTour(): void {
    if (this.tourMenuCaptured && this.isNarrowViewport()) {
      this.isMenuOpen = this.menuOpenBeforeTour;
    }
    this.tourMenuCaptured = false;
    this.welcomeTour.release();
  }

  public toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  public closeMenu(): void {
    this.isMenuOpen = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (event.target.innerWidth > 768) {
      this.closeMenu();
    }
  }
}
